import * as Styled from './AnalyticFunctionPicker.styles';
import { Autocomplete } from 'formik-mui';
import { Field, useFormikContext } from 'formik';
import { InputText, Text } from '@innovyze/stylovyze/lib/components';
import React, { useEffect, useMemo, useRef, useState, } from 'react';
import get from 'lodash/get';
import union from 'lodash/union';
import { usePrevious, useWindowSize } from '@innovyze/stylovyze/lib/hooks';
import { TextField } from '@mui/material';
import { useSelectAnalyticFunctions } from '@innovyze/stylovyze/lib/selectors/analyticFunctions.selectors';
const cleanDescriptionText = (description) => {
    if (!description)
        return '';
    const descArray = description.split(' ');
    const linkRegex = /http/;
    const semiRegex = /;/;
    let link;
    const cleanedArray = descArray.map(word => {
        if (linkRegex.test(word)) {
            link = word;
            return '';
        }
        else {
            return word.replace(semiRegex, '.');
        }
    });
    const handleLink = (e) => {
        e.preventDefault();
        window.open(link, '_blank');
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Text, { variant: "text-medium", color: "textSecondary", style: { display: 'block' } }, cleanedArray.join(' ')),
        //@ts-ignore This is just straight up wrong for some reason
        link && (React.createElement(React.Fragment, null,
            React.createElement("br", null),
            React.createElement("a", { href: "#", onClick: handleLink, rel: "noopener noreferrer", style: { fontSize: '.8rem' } }, "More Information")))));
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AnalyticFunctionPicker = ({ classes, name = 'analyticFunction', onSelect, listOverride, disableAnalyticFunction, nestParams = false, paramNameRoot = '', enableFieldValueOverride = true, }) => {
    const { initialValues, values, errors, setFieldValue } = useFormikContext();
    const _analyticFunctions = useSelectAnalyticFunctions();
    const analyticFunctions = listOverride || _analyticFunctions;
    const [optionWidth, setOptionWidth] = useState(undefined);
    const [paramList, setParamList] = useState([]);
    const windowSize = useWindowSize();
    const selectRef = useRef(null);
    const defaultFunction = useMemo(() => {
        const value = get(values, name, analyticFunctions[0]);
        const newAF = analyticFunctions.find(af => af.function === value);
        if (newAF) {
            return newAF;
        }
        return null;
    }, [values[name], analyticFunctions]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const generateParamFields = ({ params }, nestParams, paramNameRoot, className) => {
        if (!params || params.length === 0)
            return undefined;
        return params.map((param, index) => {
            let ParamField;
            const units = param.unit ? ` (${param.unit})` : '';
            const name = `${paramNameRoot.length ? `${paramNameRoot}.` : ''}${nestParams ? `params.${param.param}` : param.param}`;
            switch (param.type) {
                case 'integer':
                    ParamField = (React.createElement(InputText, { key: index, fieldAttrs: {
                            name,
                        }, textField: {
                            label: `${param.name}${units}`,
                            type: 'number',
                            style: {
                                width: 'calc(100% - 40px)',
                                marginBottom: '24px',
                                alignSelf: 'end',
                            },
                        }, className: className }));
                    break;
            }
            return ParamField;
        });
    };
    const [functionPicked, setFunctionPicked] = useState(defaultFunction || null);
    const previous = usePrevious({ functionPicked });
    const inputValue = useMemo(() => {
        const newAF = get(values, `${name}.analyticFunction`);
        const afName = analyticFunctions.find(af => af.function === newAF)?.name;
        return afName ?? newAF;
    }, [
        initialValues,
        initialValues[name],
        values,
        values[name],
        values.analyticFunction,
    ]);
    useEffect(() => {
        const offsetWidth = selectRef.current?.offsetWidth;
        if (offsetWidth)
            setOptionWidth(offsetWidth);
    }, [selectRef, windowSize]);
    // reduces list of analytic functions into list of its additional params
    // so values can be reset in the formik form as different functions are selected
    useEffect(() => {
        const extractParams = (acc, curr) => {
            if (curr.params) {
                const paramBuffer = [];
                curr.params.forEach(param => paramBuffer.push(`${paramNameRoot.length ? `${paramNameRoot}.` : ''}${param.param}`));
                return union(acc, paramBuffer);
            }
            else
                return acc;
        };
        if (analyticFunctions)
            setParamList(analyticFunctions.reduce(extractParams, []));
    }, [values.analyticFunction, analyticFunctions]);
    // Sets default value for remaining params and scrubs
    // values object of param values that are no longer needed
    useEffect(() => {
        let leftoverKeys = [...paramList];
        if (functionPicked?.params) {
            functionPicked.params.forEach(param => {
                const name = `${paramNameRoot.length ? `${paramNameRoot}.` : ''}${nestParams ? `params.${param.param}` : param.param}`;
                if (previous && typeof previous.functionPicked === 'object')
                    setFieldValue(name, param.default || '');
                leftoverKeys = leftoverKeys.filter(leftover => leftover !== name);
            });
        }
        leftoverKeys.forEach(leftover => setFieldValue(leftover, undefined));
    }, [functionPicked]);
    const handleSelect = (af) => {
        setFieldValue(name, af.function);
        setFunctionPicked(af);
        if (onSelect)
            onSelect(af);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Field, { id: "analyticFunction", name: name, className: classes?.picker, component: Autocomplete, value: functionPicked, inputValue: inputValue, onInputChange: (e, newValue) => {
                /**
                 * I think this function should be removed, I am not sure if it can break anything so I did a workaround
                 * it is setting the field value with label value which may be different that the analyticFunction value
                 */
                if (enableFieldValueOverride)
                    setFieldValue(name, newValue);
            }, options: analyticFunctions, onChange: (e, af) => {
                // Select
                if (af) {
                    handleSelect(af);
                }
                // Clear Button
                else {
                    setFieldValue(name, null);
                    setFunctionPicked(null);
                }
            }, getOptionLabel: (option) => option.name || '', getOptionDisabled: (option) => option.function === disableAnalyticFunction ||
                disableAnalyticFunction?.includes(option.function), renderOption: (_, af) => (React.createElement(Styled.AnalyticFunctionOption, { key: af.name, optionWidth: optionWidth, onClick: () => {
                    handleSelect(af);
                } },
                React.createElement(Styled.AnalyticFunctionTitle, { variant: "text-large" }, af.name),
                cleanDescriptionText(af.description))), "data-cy": "analytic-function-picker", "error-text": !!errors[name] + '', renderInput: (params) => (React.createElement(TextField, { ...params, "data-cy": "analytic-function-picker-input", label: "Function", variant: "outlined", value: values[name], error: !!errors[name], helperText: errors[name], inputProps: {
                    ...params.inputProps,
                    'aria-autocomplete': 'none',
                }, required: true })) }),
        functionPicked &&
            generateParamFields(functionPicked, nestParams, paramNameRoot, classes?.param)));
};
export default React.memo(AnalyticFunctionPicker);
